import { Components } from '@/api/generated/api';
import { SchoolClassType } from '@/api/generated/api.enum';
import { useSchoolClasses } from '@/composables/useSchoolClasses';
import { useCurrentClassStore } from '@/stores/useCurrentClassStore';
import { useLandingPreferenceStore } from '@/stores/useLandingPreferenceStore';
import { useLastViewedStore } from '@/stores/useLastViewedStore';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import {
  getPreferredClassOrGroupFromLandingPreference,
  getPreferredClassOrGroup,
  getTenantScopedClassOrGroup,
} from '@/utils/schoolClass.utils';
import { storeToRefs } from 'pinia';
import { computed, watchEffect } from 'vue';

export function useCurrentClass() {
  const lastViewedStore = useLastViewedStore();
  const currentClassStore = useCurrentClassStore();
  const landingPreferenceStore = useLandingPreferenceStore();
  const whoAmIStore = useWhoAmIStore();
  const { classesOrGroups } = useSchoolClasses();

  const { currentClass: storedCurrentClass } = storeToRefs(currentClassStore);
  const currentTenantId = computed<number | null>(() => whoAmIStore.whoAmI?.currentTenantId ?? null);

  const currentClass = computed<Components.Schemas.SchoolClassDto | null>(() => {
    return getTenantScopedClassOrGroup(storedCurrentClass.value, currentTenantId.value);
  });

  const currentClassId = computed<number | null>(() => currentClass.value?.id ?? null);

  const currentClassType = computed<Components.Schemas.SchoolClassType>(
    () => currentClass.value?.type ?? SchoolClassType.class,
  );

  const getValidClass = (
    schoolClass?: Components.Schemas.SchoolClassDto | null,
  ): Components.Schemas.SchoolClassDto | undefined => {
    const userId = whoAmIStore.whoAmI?.id;
    const tenantId = currentTenantId.value;
    const landingPreference = landingPreferenceStore.getPreference(userId, tenantId);

    return (
      classesOrGroups.value?.find((sc) => sc.id === schoolClass?.id) ??
      getPreferredClassOrGroupFromLandingPreference(classesOrGroups.value, landingPreference) ??
      getPreferredClassOrGroup(classesOrGroups.value)
    );
  };

  const setCurrentClass = (schoolClass?: Components.Schemas.SchoolClassDto): void => {
    const validClass = getValidClass(schoolClass);
    // Only an actual class switch clears the per-class selections. The initial null -> class
    // assignment (e.g. the boot right after onboarding, where the class only becomes valid once the
    // class list has loaded) must keep state seeded beforehand, like the demo-class landing tab.
    if (currentClass.value != null && validClass?.id !== currentClass.value.id) {
      lastViewedStore.$reset();
    }
    currentClassStore.setCurrentClass(validClass ?? null);
  };

  const syncCurrentClass = (): void => {
    const validClass = getValidClass(currentClass.value);
    if (validClass?.id !== currentClass.value?.id) {
      setCurrentClass(validClass);
    } else {
      currentClassStore.updateCurrentClass(validClass ?? null);
    }
  };

  // Check if the currentClass still exists -> cause it might be deleted in the meantime
  watchEffect(() => {
    if (!storedCurrentClass.value) return;

    if (
      currentTenantId.value != null &&
      storedCurrentClass.value.tenantId != null &&
      storedCurrentClass.value.tenantId !== currentTenantId.value
    ) {
      currentClassStore.setCurrentClass(null);
      lastViewedStore.$reset();
      return;
    }

    if (!classesOrGroups.value) return;

    const stillExists = classesOrGroups.value.some((sc) => sc.id === storedCurrentClass.value?.id);
    if (!stillExists) {
      setCurrentClass(undefined);
    }
  });

  return {
    currentClass,
    currentClassId,
    currentClassType,
    syncCurrentClass,
    setCurrentClass,
  };
}
